import { getPrefs, type DateFormat } from './prefs.js';

export const genId = (prefix = 'ID'): string =>
  `${prefix}-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

// DOM read helper for modals/forms that read uncontrolled-input values on submit.
// Used by modal components in components/modals/ and the KYC evidence form in
// pages/CaseDetail/tabs/KycTab.tsx.
export const $ = (id: string) => document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

// #399 — these two are the app's ONLY timestamp formatters; every page renders
// dates through them, which is what makes the timezone and date-format
// preferences a change here rather than a sweep through 30 components.
//
// They read the preference store at CALL time, not at import time. A formatter
// that captured the settings once would keep rendering the old timezone until
// a reload, which is the one moment a user is watching to see whether the
// setting took.
//
// getPrefs() never throws and always returns a complete object (invalid stored
// values are coerced away at the boundary), so there is no fallback path here
// to get wrong. The defaults — no timezone override, 'us', '12h' — reproduce
// the pre-#399 output exactly.

const DATE_STYLE: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  // "Mar 12, 2026" — the pre-#399 format, and still the default.
  us: { month: 'short', day: 'numeric', year: 'numeric' },
  // "2026-03-12". Unambiguous across the US/EU day-month split, which matters
  // on a screen shared with CTIA.
  iso: { year: 'numeric', month: '2-digit', day: '2-digit' },
  long: { month: 'long', day: 'numeric', year: 'numeric' },
};

// `iso` is the one style whose separator the en-US locale would get wrong
// (it renders 2-digit/2-digit/numeric as 03/12/2026), so it formats through
// en-CA, whose short date IS ISO-ordered.
const localeFor = (format: DateFormat): string => (format === 'iso' ? 'en-CA' : 'en-US');

const zoneOf = (): { timeZone?: string } => {
  const tz = getPrefs().timezone;
  return tz ? { timeZone: tz } : {};
};

export const fmtDate = (d: string | Date | null | undefined): string => {
  if (!d) return '—';
  const { dateFormat } = getPrefs();
  return new Date(d).toLocaleDateString(localeFor(dateFormat), {
    ...DATE_STYLE[dateFormat],
    ...zoneOf(),
  });
};

export const fmtDateTime = (d: string | Date | null | undefined): string => {
  if (!d) return '—';
  const { dateFormat, clockFormat } = getPrefs();
  return new Date(d).toLocaleString(localeFor(dateFormat), {
    ...DATE_STYLE[dateFormat],
    hour: clockFormat === '24h' ? '2-digit' : 'numeric',
    minute: '2-digit',
    // hour12 must be set explicitly rather than left to the locale: en-US
    // defaults to 12-hour, so a user who asked for 24-hour would silently keep
    // getting AM/PM.
    hour12: clockFormat === '12h',
    ...zoneOf(),
  });
};

// UTC wall-clock with seconds, e.g. "2026-07-16 17:00:09 UTC" — for platform
// operations (CDR pulls) where the CTIA-facing timeline must be unambiguous.
export const fmtDateTimeUtc = (d: string | Date | null | undefined): string =>
  d ? new Date(d).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '—';

// Elapsed whole days between two instants, rounded up. Pure epoch-millisecond
// arithmetic — no calendar accessors, so it is already timezone-independent
// and needs no UTC counterpart (#286 audit).
export const daysBetween = (a: string | Date, b: string | Date): number =>
  Math.ceil((new Date(b).getTime() - new Date(a).getTime()) / 86400000);

// ---------------------------------------------------------------------------
// SLA date arithmetic. MIRROR of apps/api/src/lib/sla-helpers.ts — keep
// behaviorally identical; change one side and you change the other.
//
// All three walk the calendar in UTC (#286). Every caller serialises the result
// with .toISOString() — i.e. reads it back as UTC — so UTC arithmetic is what
// makes the stored value agree with the arithmetic that produced it. A
// local-time walk (getDay/setDate) starts a day off whenever the start
// instant's local date differs from its UTC date, applies the weekend test to
// the wrong day, and lands stored deadlines on Saturdays. CI runs TZ=UTC, where
// local === UTC, so only the timezone-sweeping tests catch a regression here.
// ---------------------------------------------------------------------------

export const businessDaysBetween = (start: string | Date, end: string | Date): number => {
  let count = 0;
  const d = new Date(start);
  const target = new Date(end);
  while (d < target) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
};

export const addBusinessDays = (start: string | Date, days: number): Date => {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) added++;
  }
  return d;
};

// #850/#856 — the registrant-notice window, in CALENDAR days from the decision.
// sendNotification stamps notification_deadline with it, and the five pages
// that show the countdown derive the same instant from it. The API's never-sent
// scan has its own copy (NOTICE_WINDOW_CALENDAR_DAYS in
// apps/api/src/workers/handlers/sla-scan.ts). Two disk-read pins tie them:
// lifecycle.test.ts — "matches the API's NOTICE_WINDOW_CALENDAR_DAYS, read out
// of the API source, and sendNotification still applies it" — and
// sla-scan.test.ts — "derives the same window the SPA's sendNotification
// writes, read out of its source". Both read THIS line, so it must stay
// `export const NOTICE_WINDOW_CALENDAR_DAYS = <n>;` on one line
// (docs/CROSS_PACKAGE_CONSTANTS.md).
export const NOTICE_WINDOW_CALENDAR_DAYS = 30;

export const addCalendarDays = (start: string | Date, days: number): Date => {
  const d = new Date(start);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
};

export const generateSettlementEID = (registrantName?: string | null): string => {
  const prefix = 'BCID';
  const entityCode = (registrantName || 'ENT')
    .replace(/[^A-Z0-9]/gi, '')
    .slice(0, 4)
    .toUpperCase()
    .padEnd(4, 'X');
  const timestamp = Date.now().toString(36).toUpperCase().slice(-6);
  const check = Math.random().toString(36).slice(2, 4).toUpperCase();
  return `${prefix}-${entityCode}-${timestamp}-${check}`;
};
